import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import asyncService from '../../../../datahub/asyncService';
import selectUser from '../../../store/selectors/appSelector';
import { selectFetchedProfilePictureId } from '../../../store/selectors/userListSelector';
import {
  clearUserProfilePicture,
  getUserProfilePicture,
  getUserProfilePictureError,
  getUserProfilePictureProcessed,
} from '../usersListReducer';

export const PROFILE_PICTURE_FETCH_ACTIONS = {
  SKIP: 'skip',
  FETCH: 'fetch',
  CLEAR: 'clear',
};

export const getProfilePictureFetchAction = ({
  userId,
  profilePictureId,
  fetchedProfilePictureId,
}) => {
  if (!userId) {
    return { type: PROFILE_PICTURE_FETCH_ACTIONS.SKIP };
  }

  const nextId = profilePictureId || null;
  const currentId = fetchedProfilePictureId || null;

  if (nextId === currentId) {
    return { type: PROFILE_PICTURE_FETCH_ACTIONS.SKIP };
  }

  if (!nextId) {
    return { type: PROFILE_PICTURE_FETCH_ACTIONS.CLEAR };
  }

  return { type: PROFILE_PICTURE_FETCH_ACTIONS.FETCH, documentId: nextId };
};

const useFetchUserProfilePicture = (selectedUser) => {
  const dispatch = useDispatch();
  const sessionUser = useSelector(selectUser);
  const fetchedProfilePictureId = useSelector(selectFetchedProfilePictureId);
  const userId = selectedUser?.id;
  const profilePictureId = selectedUser?.profilePictureId;
  const { token, refreshToken } = sessionUser || {};
  const activeRequestIdRef = useRef(null);

  useEffect(() => {
    const action = getProfilePictureFetchAction({
      userId,
      profilePictureId,
      fetchedProfilePictureId,
    });

    if (action.type === PROFILE_PICTURE_FETCH_ACTIONS.SKIP) {
      return;
    }

    if (action.type === PROFILE_PICTURE_FETCH_ACTIONS.CLEAR) {
      activeRequestIdRef.current = null;
      dispatch(clearUserProfilePicture());
      return;
    }

    const requestDocumentId = action.documentId;
    activeRequestIdRef.current = requestDocumentId;
    dispatch(getUserProfilePicture({ documentId: action.documentId }));

    asyncService({
      httpMethod: 'POST',
      route: 'documents/s3/presign-download-url',
      data: { documentId: requestDocumentId },
      onSuccess: (response) => {
        if (activeRequestIdRef.current !== requestDocumentId) {
          return;
        }
        dispatch(
          getUserProfilePictureProcessed({
            documentId: requestDocumentId,
            data: { presignedUrl: response?.data?.presignedUrl || null },
          })
        );
      },
      onError: () => {
        if (activeRequestIdRef.current !== requestDocumentId) {
          return;
        }
        dispatch(getUserProfilePictureError({ documentId: requestDocumentId }));
      },
      dispatch,
      token,
      refreshToken,
    });
  }, [dispatch, fetchedProfilePictureId, profilePictureId, refreshToken, token, userId]);

  useEffect(
    () => () => {
      activeRequestIdRef.current = null;
      dispatch(clearUserProfilePicture());
    },
    [dispatch]
  );
};

export default useFetchUserProfilePicture;
